import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';

/**
 * WhiteBlock — a plain white spacer band. Intended to sit above any hero
 * section as the white backdrop that the fixed Header floats over (the
 * Header is `position: fixed; z-50` with no spacer of its own). The
 * min-height must match the Header so the hero sits flush beneath the nav
 * — tall enough to fully cover it, but no taller (a taller block leaves a
 * visible white strip between the nav and the hero).
 *
 * Weidner Oasis fork: forked from the base oasis WhiteBlock solely to
 * re-tune the min-heights to THIS fork's Header (src/components/Header.tsx),
 * whose main nav bar is `h-20 lg:h-24` — shorter than the base oasis
 * `h-24`/`h-32`. Matching dimensions:
 *   - Mobile: main nav row only, `h-20` (80px); utility row is hidden.
 *   - lg+:    utility row (~52px: `py-2.5` 20px + ~32px link) + main nav
 *             row `lg:h-24` (96px) ≈ 148px.
 *
 * Plus the optional SpecialOfferBanner, whose height the Header tracks via
 * `--offer-banner-height` and shifts down by — so this block mirrors that
 * offset so both stretch in lock-step when the banner is shown.
 *
 * Carries one optional rich-text token (centered) so editors can drop a
 * short headline into the band; otherwise it renders as pure white space.
 */
export function WhiteBlock() {
  return (
    <section
      className="oasis-white-block w-full bg-white flex items-center justify-center"
    >
      <RichTextContent
        path="whiteblock.content"
        label="Content"
        defaultValue=""
        as="div"
        className="font-body text-base text-[var(--vibe-color-text)] text-center px-6"
      />
      <style>{`
        .oasis-white-block {
          /* Mobile: match the 80px main nav row (utility row hidden). */
          min-height: calc(var(--offer-banner-height, 0px) + 80px);
          transition: min-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @media (min-width: 1024px) {
          .oasis-white-block {
            /* lg+: match the ~52px utility row + 96px main nav (≈148px). */
            min-height: calc(var(--offer-banner-height, 0px) + 148px);
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'WhiteBlock',
  displayName: 'White Block',
  tokens: {
    'whiteblock.content': {
      label: 'Content',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {},
};

(WhiteBlock as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default WhiteBlock;
